import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Box,
    Typography,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider
} from '@mui/material';
import { Menu as MenuIcon, Close } from '@mui/icons-material';
import { PALETTE, BORDERS, TINTS } from '../theme';
import { useAuth } from '../auth/AuthContext';
import AccountMenu from '../auth/AccountMenu.jsx';

const NAV_LINKS = [
    { to: '/travel-maps',  label: 'Travel Maps' },
    { to: '/trail-maps',   label: 'Trail Maps' },
    { to: '/road-trips',   label: 'Road Trips' },
    { to: '/photo-shaper', label: 'Photo Shaper' }
];

const linkBaseStyles = {
    px: 1.5,
    py: 0.75,
    borderRadius: 1.5,
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '0.95rem',
    color: PALETTE.textBody,
    transition: 'color 0.15s ease, background-color 0.15s ease',
    '&:hover': { color: PALETTE.text, backgroundColor: TINTS.textHover }
};

const TopNav = () => {
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const isActive = (to) => location.pathname.startsWith(to);
    const { isSignedIn, account, openAuthDialog, logout } = useAuth();

    return (
        <>
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'saturate(180%) blur(10px)',
                    WebkitBackdropFilter: 'saturate(180%) blur(10px)',
                    borderBottom: BORDERS.soft,
                    color: PALETTE.text
                }}
            >
                <Toolbar
                    sx={{
                        maxWidth: 1200,
                        width: '100%',
                        mx: 'auto',
                        px: { xs: 2, sm: 3 },
                        minHeight: { xs: 56, sm: 64 }
                    }}
                >
                    <Box
                        component={RouterLink}
                        to="/"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            textDecoration: 'none',
                            color: 'inherit',
                            flexGrow: 1
                        }}
                    >
                        <Box
                            component="img"
                            src="/brand/logo.png"
                            alt="Map Shaper"
                            sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'block' }}
                        />
                        <Typography
                            sx={{
                                fontWeight: 800,
                                fontSize: { xs: '1.05rem', sm: '1.15rem' },
                                letterSpacing: '-0.01em',
                                color: PALETTE.text
                            }}
                        >
                            Map Shaper
                        </Typography>
                    </Box>

                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                        {NAV_LINKS.map((link) => (
                            <Box
                                key={link.to}
                                component={RouterLink}
                                to={link.to}
                                sx={{
                                    ...linkBaseStyles,
                                    color: isActive(link.to) ? PALETTE.text : linkBaseStyles.color,
                                    backgroundColor: isActive(link.to) ? TINTS.primary : 'transparent'
                                }}
                            >
                                {link.label}
                            </Box>
                        ))}
                        {isSignedIn && (
                            <Box
                                component={RouterLink}
                                to="/my-maps"
                                sx={{
                                    ...linkBaseStyles,
                                    color: isActive('/my-maps') ? PALETTE.text : linkBaseStyles.color,
                                    backgroundColor: isActive('/my-maps') ? TINTS.primary : 'transparent'
                                }}
                            >
                                My Maps
                            </Box>
                        )}
                        <Box sx={{ ml: 0.75 }}>
                            <AccountMenu />
                        </Box>
                    </Box>

                    <IconButton
                        onClick={() => setMobileOpen(true)}
                        sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: PALETTE.text }}
                        aria-label="Open menu"
                    >
                        <MenuIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                PaperProps={{ sx: { width: 260 } }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                    <IconButton onClick={() => setMobileOpen(false)} aria-label="Close menu">
                        <Close />
                    </IconButton>
                </Box>
                <Divider />
                <List>
                    {NAV_LINKS.map((link) => (
                        <ListItem key={link.to} disablePadding>
                            <ListItemButton
                                component={RouterLink}
                                to={link.to}
                                onClick={() => setMobileOpen(false)}
                                selected={isActive(link.to)}
                            >
                                <ListItemText
                                    primary={link.label}
                                    primaryTypographyProps={{
                                        fontWeight: 600,
                                        color: PALETTE.text
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>

                <Divider />
                <List>
                    {isSignedIn ? (
                        <>
                            <ListItem sx={{ py: 0.5 }}>
                                <ListItemText
                                    primary="Signed in as"
                                    secondary={account.email}
                                    primaryTypographyProps={{ variant: 'caption', color: PALETTE.textMuted }}
                                    secondaryTypographyProps={{
                                        fontWeight: 700,
                                        color: PALETTE.text,
                                        sx: { wordBreak: 'break-all' }
                                    }}
                                />
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton
                                    component={RouterLink}
                                    to="/my-maps"
                                    onClick={() => setMobileOpen(false)}
                                    selected={isActive('/my-maps')}
                                >
                                    <ListItemText
                                        primary="My Maps"
                                        primaryTypographyProps={{ fontWeight: 600, color: PALETTE.text }}
                                    />
                                </ListItemButton>
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={async () => {
                                        setMobileOpen(false);
                                        await logout();
                                    }}
                                >
                                    <ListItemText
                                        primary="Log out"
                                        primaryTypographyProps={{ fontWeight: 600, color: PALETTE.text }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        </>
                    ) : (
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={() => {
                                    setMobileOpen(false);
                                    openAuthDialog();
                                }}
                            >
                                <ListItemText
                                    primary="Log in"
                                    primaryTypographyProps={{ fontWeight: 600, color: PALETTE.text }}
                                />
                            </ListItemButton>
                        </ListItem>
                    )}
                </List>
            </Drawer>
        </>
    );
};

export default TopNav;
